<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ReceiptThumb from './ReceiptThumb.vue';
import {
  receiptAccept,
  validateReceipt,
  uploadReceipt,
  receiptBlob,
  receiptError,
} from './receipts';
const props = defineProps({
  accountId: { type: [String, Number], required: true },
  grantId: { type: [String, Number], default: null },
  receipts: { type: Array, default: () => [] },
  disabled: Boolean,
  compact: Boolean,
});
const emit = defineEmits(['saved', 'denied', 'busy']);
const picker = ref(null);
const entries = ref([]);
const saved = ref([]);
const busy = ref(false);
const error = ref('');
const dragDepth = ref(0);
const dragging = computed(
  () => dragDepth.value > 0 && !props.disabled && !busy.value
);
const viewer = ref(null);
const viewing = ref(null);
const viewUrl = ref('');
const viewLoading = ref(false);
const viewError = ref('');
let abort = new AbortController();
let viewAbort = new AbortController();
let generation = 0;
const savedItems = computed(() => [
  ...new Map(
    [...props.receipts, ...saved.value].map(item => [item.id, item])
  ).values(),
]);
const pendingItems = computed(() =>
  entries.value.filter(item => item.status !== 'done')
);
const count = computed(
  () => savedItems.value.length + pendingItems.value.length
);
function clearView() {
  viewAbort.abort();
  viewAbort = new AbortController();
  if (viewUrl.value) URL.revokeObjectURL(viewUrl.value);
  viewUrl.value = '';
  viewing.value = null;
  viewError.value = '';
  viewLoading.value = false;
}
function reset() {
  dragDepth.value = 0;
  generation += 1;
  abort.abort();
  abort = new AbortController();
  entries.value.forEach(item => {
    if (item.url) URL.revokeObjectURL(item.url);
  });
  entries.value = [];
  saved.value = [];
  error.value = '';
  busy.value = false;
  viewer.value?.close();
  clearView();
  emit('busy', false);
}
async function openReceipt(item) {
  // Open during the tap: Safari otherwise blocks an async PDF window.
  const pdfWindow =
    item?.content_type === 'application/pdf'
      ? window.open('about:blank', '_blank')
      : null;
  if (pdfWindow) pdfWindow.opener = null;
  clearView();
  viewing.value = item;
  viewLoading.value = true;
  const current = viewAbort.signal;
  await nextTick();
  viewer.value?.open();
  try {
    const blob = await receiptBlob(
      props.accountId,
      props.grantId || item.grantId,
      item.id,
      'original',
      current
    );
    if (!current.aborted) {
      viewUrl.value = URL.createObjectURL(blob);
      if (pdfWindow && !pdfWindow.closed)
        pdfWindow.location.replace(viewUrl.value);
    } else if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
  } catch (failure) {
    if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
    if (!current.aborted) {
      viewError.value = 'Не удалось открыть чек. Попробуйте ещё раз.';
      if ([401, 403].includes(failure.response?.status)) emit('denied');
    }
  } finally {
    if (!current.aborted) viewLoading.value = false;
  }
}
// At most five files, deliberately sequential to bound server load.
/* eslint-disable no-restricted-syntax, no-continue, no-await-in-loop, no-loop-func */
async function upload(grantId = props.grantId) {
  if (busy.value) return false;
  const current = generation;
  busy.value = true;
  emit('busy', true);
  try {
    for (const item of entries.value) {
      if (item.status === 'done') continue;
      item.status = 'uploading';
      item.error = '';
      try {
        const receipt = await uploadReceipt(
          props.accountId,
          grantId,
          item,
          abort.signal,
          value => {
            if (current === generation) item.progress = value;
          }
        );
        if (current !== generation) return false;
        item.status = 'done';
        item.progress = 100;
        saved.value = [
          ...saved.value.filter(existing => existing.id !== receipt.id),
          { ...receipt, grantId },
        ];
        emit('saved', { grantId, receipt });
      } catch (failure) {
        if (current !== generation) return false;
        item.status = 'error';
        item.error = receiptError(failure);
        item.uncertain =
          !failure.response ||
          failure.response.status >= 500 ||
          [408, 429].includes(failure.response.status);
        if ([401, 403].includes(failure.response?.status)) emit('denied');
        return false;
      }
    }
    return true;
  } finally {
    if (current === generation) {
      busy.value = false;
      emit('busy', false);
    }
  }
}
/* eslint-enable no-restricted-syntax, no-continue, no-await-in-loop, no-loop-func */
async function addFiles(files) {
  error.value = '';
  if (props.disabled || busy.value) return;
  try {
    if (count.value + files.length > 5)
      throw new Error('Можно прикрепить до 5 чеков к размещению.');
    files.forEach(validateReceipt);
    entries.value.push(
      ...files.map(file => ({
        key: crypto.randomUUID(),
        file,
        status: 'queued',
        progress: 0,
        error: '',
        uncertain: false,
        url: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      }))
    );
    if (props.grantId && files.length) await upload();
  } catch (failure) {
    error.value = failure.message;
  }
}
function select(event) {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  return addFiles(files);
}
function isFileDrag(event) {
  return Array.from(event.dataTransfer?.types || []).includes('Files');
}
function dragEnter(event) {
  if (!isFileDrag(event)) return;
  event.preventDefault();
  event.stopPropagation();
  dragDepth.value += 1;
}
function dragOver(event) {
  if (!isFileDrag(event)) return;
  event.preventDefault();
  event.stopPropagation();
  event.dataTransfer.dropEffect =
    props.disabled || busy.value || count.value >= 5 ? 'none' : 'copy';
}
function dragLeave(event) {
  if (!isFileDrag(event) && !dragDepth.value) return;
  event.stopPropagation();
  dragDepth.value = Math.max(0, dragDepth.value - 1);
}
function drop(event) {
  dragDepth.value = 0;
  if (!isFileDrag(event)) return;
  event.preventDefault();
  event.stopPropagation();
  if (props.disabled || busy.value) return;
  const items = Array.from(event.dataTransfer.items || []);
  if (items.some(item => item.webkitGetAsEntry?.()?.isDirectory)) {
    error.value = 'Перетащите отдельные файлы чеков, а не папку.';
    return;
  }
  const files = Array.from(event.dataTransfer.files || []);
  if (!files.length) {
    error.value = 'Не удалось получить файлы. Нажмите кнопку выбора чека.';
    return;
  }
  addFiles(files);
}
watch(
  () => props.disabled || busy.value,
  () => {
    dragDepth.value = 0;
  }
);
function remove(item) {
  if (busy.value || props.disabled || item.uncertain) return;
  if (item.url) URL.revokeObjectURL(item.url);
  entries.value = entries.value.filter(entry => entry.key !== item.key);
}
watch(() => props.accountId, reset);
onBeforeUnmount(reset);
defineExpose({ upload, reset, hasFiles: () => entries.value.length > 0 });
</script>

<template>
  <div
    class="flex flex-col gap-2 min-w-0 rounded-lg border border-dashed transition-colors"
    :class="[
      compact ? 'p-2' : 'p-4',
      dragging
        ? 'border-n-brand bg-n-blue-2 ring-2 ring-n-brand'
        : 'border-n-weak',
    ]"
    :aria-busy="busy"
    :aria-label="compact ? 'Чеки размещения' : 'Подтверждение оплаты'"
    @dragenter="dragEnter"
    @dragover="dragOver"
    @dragleave="dragLeave"
    @drop="drop"
  >
    <span v-if="!compact" class="text-sm font-medium text-n-slate-12">
      Подтверждение оплаты
    </span>
    <p
      class="text-xs text-n-slate-11 m-0"
      :class="dragging ? '' : 'hidden sm:block'"
      aria-live="polite"
    >
      {{
        dragging
          ? 'Отпустите файлы здесь'
          : compact
            ? 'Перетащите чек сюда'
            : 'Перетащите чеки сюда или выберите файлы'
      }}
    </p>
    <div class="flex flex-wrap gap-2 items-center">
      <ReceiptThumb
        v-for="item in savedItems"
        :key="`${accountId}-${item.id}`"
        :account-id="accountId"
        :grant-id="grantId || item.grantId"
        :item="item"
        @open="openReceipt"
        @denied="emit('denied')"
      />
      <input
        ref="picker"
        type="file"
        :accept="receiptAccept"
        multiple
        class="hidden"
        tabindex="-1"
        :disabled="disabled || busy || count >= 5"
        aria-label="Выбрать чек из файлов или фото"
        @change="select"
      />
      <Button
        v-if="count < 5"
        type="button"
        slate
        faded
        :icon="savedItems.length ? 'i-lucide-plus' : 'i-lucide-paperclip'"
        :label="
          savedItems.length ? '' : compact ? 'Добавить чек' : 'Прикрепить чек'
        "
        :aria-label="
          savedItems.length
            ? 'Добавить ещё чек'
            : compact
              ? 'Добавить чек'
              : 'Прикрепить чек'
        "
        class="min-h-11 min-w-11"
        :class="compact ? '!whitespace-normal' : 'w-full sm:w-auto'"
        :disabled="disabled || busy"
        @click="picker?.click()"
      />
    </div>
    <p v-if="!compact" class="text-xs text-n-slate-11 m-0">
      Необязательно · PDF, JPG, PNG, WebP · до 5 файлов по 10 МБ
    </p>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-9 m-0 break-words">
      {{ error }}
    </p>
    <div
      v-for="item in pendingItems"
      :key="item.key"
      class="border border-n-weak rounded-lg p-2 flex flex-col gap-2 min-w-0"
    >
      <div class="flex items-center gap-2 min-w-0">
        <img
          v-if="item.url"
          :src="item.url"
          alt=""
          class="w-12 h-12 object-contain shrink-0 rounded"
        />
        <span v-else class="text-xs text-n-slate-11">PDF</span>
        <span class="text-xs text-n-slate-12 break-all">{{
          item.file.name
        }}</span>
      </div>
      <template v-if="item.status === 'uploading'">
        <progress
          :value="item.progress"
          max="100"
          class="w-full"
          aria-label="Загрузка чека"
        />
        <span role="status" class="text-xs text-n-slate-11">{{
          item.progress >= 99 ? 'Сохраняем чек…' : `Загрузка: ${item.progress}%`
        }}</span>
      </template>
      <p
        v-if="item.error"
        role="alert"
        class="text-xs text-n-ruby-9 m-0 break-words"
      >
        {{ item.error }}
      </p>
      <Button
        v-if="!item.uncertain && item.status !== 'uploading'"
        type="button"
        label="Убрать файл"
        ghost
        slate
        :disabled="disabled || busy"
        @click="remove(item)"
      />
    </div>
    <Button
      v-if="grantId && pendingItems.length && !busy"
      type="button"
      label="Повторить"
      slate
      faded
      :disabled="disabled"
      @click="upload()"
    />
    <Dialog
      ref="viewer"
      :title="viewing?.filename || 'Чек'"
      width="2xl"
      confirm-button-label="Закрыть"
      :show-cancel-button="false"
      overflow-y-auto
      @confirm="viewer?.close()"
      @close="clearView"
    >
      <p v-if="viewLoading" role="status" class="text-sm text-n-slate-11">
        Загрузка чека…
      </p>
      <p v-if="viewError" role="alert" class="text-sm text-n-ruby-9">
        {{ viewError }}
      </p>
      <Button
        v-if="viewError"
        type="button"
        label="Повторить открытие"
        slate
        faded
        @click="openReceipt(viewing)"
      />
      <template v-if="viewUrl">
        <div
          v-if="viewing?.content_type === 'application/pdf'"
          class="rounded-xl border border-n-weak p-5 flex flex-col gap-3 text-sm text-n-slate-12"
        >
          <span class="i-lucide-file-text text-3xl" />
          <p class="m-0">
            PDF открывается в отдельной вкладке. Если она не появилась, нажмите
            ссылку ниже.
          </p>
        </div>
        <img
          v-else
          :src="viewUrl"
          alt="Подтверждение оплаты"
          class="w-full max-h-[60vh] object-contain"
        />
        <a
          :href="viewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm text-n-blue-11 underline min-h-11 flex items-center"
        >
          Открыть в отдельной вкладке
        </a>
        <a
          :href="viewUrl"
          :download="viewing?.filename"
          class="text-sm text-n-blue-11 underline min-h-11 flex items-center"
        >
          Сохранить файл
        </a>
      </template>
    </Dialog>
  </div>
</template>
